/**
 * The one place that decides what a user may do.
 *
 * A user's rights are their role's list, plus any per-user additions, minus any
 * per-user revocations:
 *
 *     effective = (role ∪ permissionsAdd) − permissionsRevoke
 *
 * The role stays the baseline, so the organisation's roles keep their meaning
 * and a person who needs one extra page carries one visible exception rather
 * than a cloned role that quietly drifts from the original.
 *
 * Deliberately free of React and of any Express or database import, because
 * both the browser (usePermission) and the server (authorize middleware) have
 * to reach the same answer. Two implementations of this rule would eventually
 * disagree, and the one that matters — the server — is the one nobody sees.
 */

import { expandWildcard } from './permissions.config';

/**
 * Categories that may not be adjusted per user.
 *
 * These are decisions about the organisation rather than about a person, so
 * they belong to the role and nowhere else:
 *
 *   Users & Roles   overriding this is a privilege-escalation route — someone
 *                   granted admin.role:edit as a personal exception could then
 *                   grant themselves anything at all
 *   System          database, app settings and appearance affect every user
 *   Setup — General shared lookup data, not a per-person concern
 *   Dashboard       widget visibility, adjusted by the user's own preferences
 *
 * Enforced on the server as well as hidden in the UI: hiding a field is a
 * courtesy to the person using the form, not a control over what the API
 * accepts.
 */
export const OVERRIDE_EXCLUDED_CATEGORIES = new Set([
    'Users & Roles',
    'System',
    'Setup — General',
    'Dashboard'
]);

/** Is this key one that may be overridden for an individual user? */
export function isOverridable(key: string, categoryOf: (k: string) => string | undefined): boolean {
    const category = categoryOf(key);
    return !!category && !OVERRIDE_EXCLUDED_CATEGORIES.has(category);
}

export type PermissionOverrides = {
    /** Keys granted to this user on top of their role. */
    permissionsAdd?: string[] | null;
    /** Keys taken away from this user, even if the role grants them. */
    permissionsRevoke?: string[] | null;
};

/** Expand a raw list (which may hold wildcards) into concrete keys. */
function toConcrete(raw: readonly unknown[] | null | undefined): Set<string> {
    const out = new Set<string>();
    for (const entry of raw || []) {
        if (typeof entry !== 'string' || !entry) continue;
        for (const key of expandWildcard(entry)) out.add(key);
    }
    return out;
}

/**
 * Resolve the effective permission set.
 *
 * Revocation wins over both the role and the additions: a right taken away from
 * someone is an explicit decision about that person, and should not be undone by
 * a later change to their role.
 */
export function resolveEffectivePermissions(
    rolePermissions: readonly unknown[] | null | undefined,
    overrides?: PermissionOverrides | null
): Set<string> {
    const effective = toConcrete(rolePermissions);
    for (const key of toConcrete(overrides?.permissionsAdd)) effective.add(key);
    for (const key of toConcrete(overrides?.permissionsRevoke)) effective.delete(key);
    return effective;
}

/**
 * Does this permission set satisfy `key`?
 *
 * `key` may itself be a wildcard ('service.*:cancel' asks "any service type"),
 * which is how several call sites phrase a question about a group of rights.
 */
export function grantsInclude(grants: Set<string>, key: string): boolean {
    if (grants.has(key)) return true;
    if ((key ?? '').includes('*')) {
        for (const concrete of expandWildcard(key)) {
            if (grants.has(concrete)) return true;
        }
    }
    return false;
}

/**
 * Where a user's right to `key` comes from — for showing inherited vs override
 * in the user form, so an administrator can see which rights are the role's and
 * which were decided for this person.
 */
export type PermissionOrigin = 'role' | 'added' | 'revoked' | 'none';

export function permissionOrigin(
    key: string,
    rolePermissions: readonly unknown[] | null | undefined,
    overrides?: PermissionOverrides | null
): PermissionOrigin {
    const fromRole = toConcrete(rolePermissions).has(key);
    const added = toConcrete(overrides?.permissionsAdd).has(key);
    const revoked = toConcrete(overrides?.permissionsRevoke).has(key);

    if (revoked) return 'revoked';
    if (added && !fromRole) return 'added';
    if (fromRole) return 'role';
    return 'none';
}
